import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";

export class StringMagnetic extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "magnetic";
    this._type = 2; // UI Module

    this.cssProperties = [
      { name: "--magnetic-x", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--magnetic-y", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--magnetic-target-x", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--magnetic-target-y", syntax: "<number>", initialValue: "0", inherits: true }
    ];

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "strength", type: "number", fallback: this.settings["strength"] },
      { key: "radius", type: "number", fallback: this.settings["radius"] },
    ];
  }

  override initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.isMagneting = false;
    object.magneticTargetX = 0;
    object.magneticTargetY = 0;
    object.magneticX = 0;
    object.magneticY = 0;
    object.setProperty("magnetic-lerp", 0.1);
    object.eventMagneticName = this.getObjectEventName(object, "magnetic:move");
    object.setProperty("__next-magnetic-x", 0);
    object.setProperty("__next-magnetic-y", 0);
    object.setProperty("__needs-magnetic-update", false);
  }

  onMouseMove(e: MouseEvent): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      const element = object.htmlElement as HTMLElement;
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const dx = e.clientX - centerX;
      const dy = e.clientY - centerY;
      const distance = Math.sqrt(dx ** 2 + dy ** 2);

      const radius = object.getProperty<number>("radius") ?? 0;
      const strength = object.getProperty<number>("strength") ?? 0;

      const factor = this.tools.magneticPull.process({
        distance,
        radius,
        strength,
      });

      object.magneticTargetX = dx * factor;
      object.magneticTargetY = dy * factor;
      if (factor > 0) {
        object.isMagneting = true;
      }
    }
  }

  onFrame(data: StringData): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      if (object.isMagneting) {
        let magneticX = object.magneticX;
        let magneticY = object.magneticY;

        let lerp = object.getProperty<number>("magnetic-lerp") ?? 0.1;

        let targetMagneticX = object.magneticTargetX;
        let targetMagneticY = object.magneticTargetY;

        let lerpX = this.tools.lerp.process({
          from: magneticX,
          to: targetMagneticX,
          progress: lerp,
        });
        let lerpY = this.tools.lerp.process({
          from: magneticY,
          to: targetMagneticY,
          progress: lerp,
        });

        if (lerpX > -0.01 && lerpX < 0.01) {
          lerpX = 0;
          object.magneticX = object.magneticTargetX;
        }
        if (lerpY > -0.01 && lerpY < 0.01) {
          lerpY = 0;
          object.magneticY = object.magneticTargetY;
        }
        magneticX += lerpX;
        magneticY += lerpY;
        object.magneticX = magneticX;
        object.magneticY = magneticY;

        if (object.eventMagneticName) {
          this.events.emit(object.eventMagneticName, {
            x: magneticX,
            y: magneticY,
          });
        }
        object.setProperty("__next-magnetic-x", magneticX);
        object.setProperty("__next-magnetic-y", magneticY);
        object.setProperty("__needs-magnetic-update", true);

        if (
          object.magneticTargetX === magneticX &&
          object.magneticTargetY === magneticY
        ) {
          object.isMagneting = false;
        }
      }
    }
  }

  override onMutate(): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      const needsUpdate = object.getProperty<boolean>("__needs-magnetic-update") === true;
      if (!needsUpdate) continue;

      const x = object.getProperty<number>("__next-magnetic-x") ?? object.magneticX;
      const y = object.getProperty<number>("__next-magnetic-y") ?? object.magneticY;

      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setVar(el, "--magnetic-x", x);
        styleTxn.setVar(el, "--magnetic-y", y);
      });

      object.setProperty("__needs-magnetic-update", false);
    }
  }
}
